import {
  Auth0Client,
  GenericError,
  GetTokenSilentlyOptions,
  User as Auth0User,
} from "@auth0/auth0-spa-js";
import type { User as AppUser } from "@shuttlerock/auth-types";
import { ConfigService } from "@shuttlerock/configuration";
import { useSyncExternalStore } from "use-sync-external-store/shim";
import { getUserData } from "./getUserData";

const auth0OrganizationId = ConfigService.AUTH0_ORGANIZATION_ID();

/** @internal - Not to be used outside `@shuttlerock/auth` */
const Auth0 = new Auth0Client({
  clientId: ConfigService.AUTH0_CLIENT_ID(),
  domain: ConfigService.AUTH0_DOMAIN(),
  useRefreshTokens: true,
  useRefreshTokensFallback: true,
  authorizationParams: {
    audience: ConfigService.AUTH0_AUDIENCE(),
    redirect_uri: `${ConfigService.APP_URL()}${ConfigService.AUTH0_CALLBACK_ROUTE()}`,
    supergraph_url: ConfigService.AUTH0_SUPERGRAPH_URL(),
    ...(auth0OrganizationId ? { organization: auth0OrganizationId } : {}),
    scope: `openid profile email ${ConfigService.AUTH0_SCOPES()}`,
  },
});

/** @internal - Not to be used outside `@shuttlerock/auth` */
const isAuthClientInitialized = Auth0.checkSession().then(() => true);

// The typing gets complex with `detailedResponse` so we can look to support **if** we ever have a use-case for it
export type GetTokenOptions = Omit<GetTokenSilentlyOptions, "detailedResponse">;
type Listener = (user: AppUser | undefined) => void;

/** Internal to `@shuttlerock/auth`. */
export type AuthService = {
  getUser(): AppUser | undefined;
  subscribe(listener: Listener): () => void;
  getSessionUser(): AppUser | undefined;
  subscribeSession(listener: Listener): () => void;
  /**
   * True once Auth0 has answered that the visitor has no session, until a login completes.
   * Logging out does not set it. A missing session user alone is not unauthenticated: it is
   * also the state while any token's identity is being read.
   */
  isUnauthenticated(): boolean;
  /** Resolves `undefined` when the visitor has no Auth0 session; rejects only when the lookup itself fails. */
  getAccessTokenSilently(options?: GetTokenOptions): Promise<string | undefined>;
  loginWithRedirect: typeof Auth0.loginWithRedirect;
  logout: typeof Auth0.logout;
  handleRedirectCallback: typeof Auth0.handleRedirectCallback;
};

/** Internal to `@shuttlerock/auth`. */
export const AuthClient = ((): AuthService => {
  let user: AppUser | undefined;
  const listeners = new Set<Listener>();
  let sessionUser: AppUser | undefined;
  let sessionVersion = 0;
  let authenticationGeneration = 0;
  let tokenRequest = 0;
  let newestTokenRequest = 0;
  let newestToken: string | undefined;
  const sessionListeners = new Set<Listener>();
  let unauthenticated = false;
  const publishSession = (next: AppUser | undefined) => {
    sessionUser = next;
    sessionListeners.forEach((listener) => listener(next));
  };
  // `unauthenticated` changes only here, before publishing, so session subscribers
  // always read it alongside the session it describes.
  const clearSession = (next?: { unauthenticated: boolean }) => {
    if (next) unauthenticated = next.unauthenticated;
    sessionVersion += 1;
    authenticationGeneration += 1;
    userReads.clear();
    publishSession(undefined);
  };
  // Holds the in-flight read, not its result: a cold load asks for a token from
  // every matched loader, the LaunchDarkly bootstrap and Apollo's header link at
  // once, and they must share one identity read rather than each issue their own.
  const userReads = new Map<string, Promise<AppUser | undefined>>();
  // Auth0's definite "no session" answer. Each silent attempt without a refresh
  // token is a hidden-iframe round trip the caller waits on, and none can
  // succeed until the visitor logs in, so later calls answer without asking.
  let hasNoSession = false;

  function getUser() {
    return user;
  }

  function readUser(accessToken: string) {
    const pending = userReads.get(accessToken);
    if (pending) {
      return pending;
    }

    // Prevent the cache from growing indefinitely, we only care about the most recent token.
    userReads.clear();
    const version = ++sessionVersion;
    publishSession(undefined);
    const userRead = (async () => {
      const auth0User = await Auth0.getUser();
      const next = await auth0UserToAppUser(auth0User, accessToken);
      // An old Me response cannot revive a lost session or overwrite a newer identity.
      if (version === sessionVersion) {
        user = next;
        publishSession(next);
        listeners.forEach((listener) => listener(user));
      }
      return next;
    })();
    userReads.set(accessToken, userRead);
    // A failed read is not kept, so the next caller retries it.
    userRead.catch(() => {
      if (userReads.get(accessToken) === userRead) userReads.delete(accessToken);
    });

    return userRead;
  }

  async function renewedToken(principalKey: string | undefined, generation: number) {
    // A superseded request can use a renewal only after Me proves the same principal.
    // Never hand an old caller a different user's token or revive a cleared session.
    while (principalKey && generation === authenticationGeneration) {
      const token = newestToken;
      if (!token) return undefined;
      const current = await userReads.get(token);
      if (generation !== authenticationGeneration) return undefined;
      if (token !== newestToken) continue;
      return current?.principalKey === principalKey ? token : undefined;
    }
    return undefined;
  }

  async function getAccessTokenSilently(options?: GetTokenOptions) {
    await isAuthClientInitialized;
    if (hasNoSession) {
      return undefined;
    }

    const generation = authenticationGeneration;
    const principalAtStart = sessionUser?.principalKey;
    const requestId = ++tokenRequest;
    const accessToken = await Auth0.getTokenSilently(options).catch((error: unknown) => {
      // A stale error must not invalidate a newer token or completed login callback.
      if (generation !== authenticationGeneration || requestId < newestTokenRequest)
        return undefined;
      const noSession = error instanceof GenericError && error.error === "login_required";
      if (noSession) hasNoSession = true;
      clearSession(noSession ? { unauthenticated: true } : undefined);
      if (noSession) return undefined;
      throw error;
    });
    // Token requests already in flight when logout/session loss occurs cannot revive it.
    if (generation !== authenticationGeneration) return undefined;
    if (requestId < newestTokenRequest && accessToken !== newestToken)
      return renewedToken(principalAtStart, generation);
    if (!accessToken) {
      clearSession();
      return undefined;
    }

    newestTokenRequest = Math.max(newestTokenRequest, requestId);
    newestToken = accessToken;
    const identity = await readUser(accessToken);
    if (generation !== authenticationGeneration) return undefined;
    if (accessToken !== newestToken) return renewedToken(identity?.principalKey, generation);
    return accessToken;
  }

  const logout: typeof Auth0.logout = (options) => {
    // Also reject new silent reads while Auth0 is asynchronously redirecting/clearing its cache.
    hasNoSession = true;
    clearSession();
    return Auth0.logout(options);
  };
  const loginWithRedirect: typeof Auth0.loginWithRedirect = (options) =>
    Auth0.loginWithRedirect(options);
  const handleRedirectCallback: typeof Auth0.handleRedirectCallback = async (url) => {
    const result = await Auth0.handleRedirectCallback(url);
    clearSession({ unauthenticated: false });
    hasNoSession = false;
    return result;
  };

  function subscribe(listener: Listener) {
    listeners.add(listener);
    return () => listeners.delete(listener);
  }

  return {
    getUser,
    subscribe,
    getSessionUser: () => sessionUser,
    subscribeSession(listener: Listener) {
      sessionListeners.add(listener);
      return () => sessionListeners.delete(listener);
    },
    isUnauthenticated: () => unauthenticated,
    getAccessTokenSilently,
    logout,
    loginWithRedirect,
    handleRedirectCallback,
  } as const;
})();

/** Internal to `@shuttlerock/auth`. */
export const useUser = () =>
  useSyncExternalStore(AuthClient.subscribe, AuthClient.getUser, AuthClient.getUser);

const AUTH0_USER_REGEX = /^auth0/;
const NAMESPACE = "https://cloud.shuttlerock.com/identity/claims";

const TENANCY_CLAIM = `${NAMESPACE}/tenancy_key`;

/** @internal - Not to be used outside `@shuttlerock/auth` */
export async function auth0UserToAppUser(
  auth0User: Auth0User | undefined,
  accessToken: string,
): Promise<AppUser | undefined> {
  if (!auth0User) {
    return undefined;
  }

  const userData = await getUserData(accessToken);

  const tenancyKeyFromToken = (auth0User?.[TENANCY_CLAIM] as string | undefined) ?? undefined;

  return {
    key: userData?.key,
    principalKey: userData?.principalKey,
    fullName: userData?.fullName,
    firstName: userData?.firstName,
    lastName: userData?.lastName,
    email: userData?.email,
    tenantKey: tenancyKeyFromToken,
    picture: userData?.picture,
    auth0IntegrationKey: auth0User.sub,
    isSocialUser: auth0User.sub ? !AUTH0_USER_REGEX.test(auth0User.sub) : false,
    treatments: userData?.treatments ?? [],
  };
}
